import { useCallback, useEffect, useState } from "react";
import { type StorageUser, fetchStorageUsers } from "../../api/admin-content";
import { PageHeader } from "../../components/PageHeader";
import {
  ErrorBanner,
  LoadingIndicator,
  Pagination,
  tableStyles as ts,
} from "../../components/TableParts";
import { t } from "../../i18n";
import { StorageDetailPanel } from "./StorageDetailPanel";

const PAGE_SIZE = 20;

type FilterValue = "all" | "over-quota" | "sync-error";

const FILTERS: ReadonlyArray<{ value: FilterValue; label: string }> = [
  { value: "all", label: "storage.filter_all" },
  { value: "over-quota", label: "storage.filter_over_quota" },
  { value: "sync-error", label: "storage.filter_sync_error" },
];

export function StoragePage(): React.JSX.Element {
  const [items, setItems] = useState<ReadonlyArray<StorageUser>>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [filter, setFilter] = useState<FilterValue>("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchStorageUsers({
        limit: PAGE_SIZE,
        offset,
        filter: filter === "all" ? undefined : filter,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("common.failed_to_load"));
    } finally {
      setLoading(false);
    }
  }, [offset, filter]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleFilterChange = useCallback((f: FilterValue): void => {
    setFilter(f);
    setOffset(0);
    setExpandedId(null);
  }, []);

  const handleToggle = useCallback((userId: string): void => {
    setExpandedId((prev) => (prev === userId ? null : userId));
  }, []);

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <div>
      <PageHeader
        title={t("storage.title")}
        description={t("storage.description")}
      />
      <FilterBar active={filter} onChange={handleFilterChange} />
      {error && <ErrorBanner message={error} />}
      {loading ? (
        <LoadingIndicator />
      ) : (
        <>
          <StorageTable
            items={items}
            expandedId={expandedId}
            onToggle={handleToggle}
            onActionDone={load}
          />
          <Pagination
            page={currentPage}
            totalPages={totalPages}
            onPrev={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
            onNext={() => setOffset((o) => o + PAGE_SIZE)}
          />
        </>
      )}
    </div>
  );
}

// ─── Sub-components ───

function FilterBar({
  active,
  onChange,
}: {
  active: FilterValue;
  onChange: (f: FilterValue) => void;
}): React.JSX.Element {
  return (
    <div style={styles.filterBar}>
      {FILTERS.map((f) => (
        <button
          key={f.value}
          type="button"
          onClick={() => onChange(f.value)}
          style={{
            ...ts.filterBtn,
            ...(active === f.value ? ts.filterBtnActive : {}),
          }}
        >
          {f.label}
        </button>
      ))}
    </div>
  );
}

function StorageTable({
  items,
  expandedId,
  onToggle,
  onActionDone,
}: {
  items: ReadonlyArray<StorageUser>;
  expandedId: string | null;
  onToggle: (id: string) => void;
  onActionDone: () => void;
}): React.JSX.Element {
  if (items.length === 0) {
    return <p style={ts.emptyText}>{t("storage.no_users_found")}</p>;
  }

  return (
    <table style={ts.table}>
      <thead>
        <tr>
          <th style={ts.th}>{t("storage.col_user")}</th>
          <th style={ts.th}>{t("storage.col_used_limit")}</th>
          <th style={ts.th}>{t("storage.col_msg")}</th>
          <th style={ts.th}>{t("storage.col_media")}</th>
          <th style={ts.th}>{t("storage.col_art")}</th>
          <th style={ts.th}>{t("storage.col_kb")}</th>
          <th style={ts.th}>{t("storage.col_sync")}</th>
        </tr>
      </thead>
      <tbody>
        {items.map((u) => (
          <StorageRow
            key={u.user_id}
            user={u}
            isExpanded={expandedId === u.user_id}
            onToggle={onToggle}
            onActionDone={onActionDone}
          />
        ))}
      </tbody>
    </table>
  );
}

function StorageRow({
  user,
  isExpanded,
  onToggle,
  onActionDone,
}: {
  user: StorageUser;
  isExpanded: boolean;
  onToggle: (id: string) => void;
  onActionDone: () => void;
}): React.JSX.Element {
  const overQuota = user.storage_used_bytes > user.storage_limit_bytes;

  return (
    <>
      <tr
        onClick={() => onToggle(user.user_id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") onToggle(user.user_id);
        }}
        tabIndex={0}
        aria-expanded={isExpanded}
        style={{ ...ts.row, cursor: "pointer" }}
      >
        <td style={ts.td}>
          <div style={styles.userName}>{user.display_name}</div>
          <div style={styles.mono}>{user.user_id.slice(0, 8)}</div>
        </td>
        <td style={{ ...ts.td, ...(overQuota ? styles.danger : {}) }}>
          {formatBytes(user.storage_used_bytes)} /{" "}
          {formatBytes(user.storage_limit_bytes)}
        </td>
        <td style={ts.td}>{user.object_counts.messages}</td>
        <td style={ts.td}>{user.object_counts.media}</td>
        <td style={ts.td}>{user.object_counts.artifacts}</td>
        <td style={ts.td}>{user.object_counts.knowledge}</td>
        <td style={ts.td}>
          <SyncBadge status={user.sync_status} />
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={7} style={ts.expandedCell}>
            <StorageDetailPanel
              userId={user.user_id}
              onActionDone={onActionDone}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function SyncBadge({ status }: { status: string }): React.JSX.Element {
  const colorMap: Record<string, string> = {
    synced: "var(--color-success)",
    error: "var(--color-danger)",
  };
  const color = colorMap[status] ?? "var(--color-text-secondary)";
  return <span style={{ ...ts.badge, color }}>{status}</span>;
}

// ─── Helpers ───

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / 1024 ** i;
  return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

// ─── Page-specific styles ───

const styles: Record<string, React.CSSProperties> = {
  filterBar: { display: "flex", gap: 8, marginBottom: 16 },
  userName: { fontWeight: 500 },
  mono: {
    fontSize: 12,
    color: "var(--color-text-secondary)",
    fontFamily: "monospace",
  },
  danger: { color: "var(--color-danger)", fontWeight: 600 },
};
